import { useState } from 'react';
import { router, usePage } from '@inertiajs/react';
import { CalendarX2, Undo2 } from 'lucide-react';
import { Aviso, Botao } from '@/Logigate/ui';
import { Pops } from '@/utils/Pops';
import req from '@/utils/req';

/**
 * A eliminação agendada de um módulo do Construtor no ecrã (plano 3.6): a faixa «Este módulo vai ser
 * eliminado a 27/11/2026 — Cancelar eliminação», no topo das páginas do módulo (o espaço `avisos` do
 * casco, pelo `construtor.modulo`) e na lista dos módulos. Até à data o módulo funciona como
 * sempre; quem tem a permissão de eliminar módulos cancela a qualquer momento.
 */

/** A permissão que agenda e cancela (`PermissionMap::MODULO_ELIMINAR`). */
export const PERMISSAO_ELIMINAR_MODULOS = 'construtor_modulo_eliminar';

/** O que a faixa lê de um módulo (o `toApp()` do `ModuloCustom` tem tudo). */
export interface ModuloComEliminacao {
    slug: string;
    nome: string;
    eliminacao_agendada_para?: string | null;
}

/** «27/11/2026», no fuso de Angola (a data vem em UTC do servidor). */
export function diaDaEliminacao(data?: string | null): string {
    if (!data) {
        return '';
    }
    return new Date(data).toLocaleDateString('pt-PT', { timeZone: 'Africa/Luanda', day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Se quem vê pode cancelar (a permissão de eliminar módulos, nas `permissoes` da página). */
export function usePodeCancelarEliminacao(): boolean {
    const { permissoes } = usePage<{ permissoes?: string[] }>().props;
    return Array.isArray(permissoes) && permissoes.includes(PERMISSAO_ELIMINAR_MODULOS);
}

/** Cancela a eliminação agendada. Devolve `true` se ficou cancelada. */
export async function cancelarEliminacao(raiz: string, modulo: ModuloComEliminacao): Promise<boolean> {
    const res = await req().delete(`${raiz}/modulos/${modulo.slug}/eliminacao`);
    if (res.data.estado === 'ok') {
        Pops.success(res.data.texto || 'Eliminação cancelada. O módulo fica.');
        return true;
    }
    Pops.error(res.data.texto || 'Não foi possível cancelar a eliminação. Tenta de novo.');
    return false;
}

/**
 * A faixa de um módulo com a eliminação agendada. `topo`: a toda a largura, no espaço dos avisos
 * do casco (como a da rescisão); `cartao`: um aviso dentro da página (a lista dos módulos). Sem
 * eliminação agendada não mostra nada.
 */
export default function FaixaEliminacaoAgendada({ modulo, raiz, podeCancelar, onCancelado, variante = 'topo', mostrarNome = false }: {
    modulo: ModuloComEliminacao | null | undefined;
    /** `{base_path}/construtor` */
    raiz: string;
    podeCancelar: boolean;
    /** Depois de cancelar; por omissão recarrega a página. */
    onCancelado?: () => void;
    variante?: 'topo' | 'cartao';
    /** O nome do módulo no título (na lista, onde há vários). */
    mostrarNome?: boolean;
}) {
    const [aCancelar, setACancelar] = useState(false);

    if (!modulo?.eliminacao_agendada_para) {
        return null;
    }

    const titulo = `${mostrarNome ? `O módulo «${modulo.nome}»` : 'Este módulo'} vai ser eliminado a ${diaDaEliminacao(modulo.eliminacao_agendada_para)}`;
    const texto = podeCancelar
        ? 'Até lá funciona normalmente. Nesse dia, os registos e os ficheiros saem de vez. Se não for para eliminar, cancela.'
        : 'Até lá funciona normalmente. Nesse dia, os registos e os ficheiros saem de vez. Só quem pode eliminar módulos cancela a eliminação.';

    const cancelar = async () => {
        setACancelar(true);
        const ok = await cancelarEliminacao(raiz, modulo);
        if (!ok) {
            setACancelar(false);
            return;
        }
        if (onCancelado) {
            setACancelar(false);
            onCancelado();
            return;
        }
        router.reload({ onFinish: () => setACancelar(false) });
    };

    const botao = podeCancelar && (
        <Botao tamanho="sm" variante="suave" cor="red" icone={<Undo2 className="size-4" />} aCarregar={aCancelar} onClick={cancelar} data-cancelar-eliminacao={modulo.slug}>
            Cancelar eliminação
        </Botao>
    );

    if (variante === 'cartao') {
        return (
            <div data-faixa-eliminacao={modulo.slug}>
                <Aviso cor="red" icone={<CalendarX2 />} titulo={titulo} accao={botao || undefined}>
                    {texto}
                </Aviso>
            </div>
        );
    }

    return (
        <div role="status" data-faixa-eliminacao={modulo.slug} className="border-b border-red-200 bg-red-50 text-red-950 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-50">
            <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3 px-3 py-3 sm:flex-row sm:items-center sm:px-6 lg:px-8">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300" aria-hidden>
                        <CalendarX2 className="size-[18px]" />
                    </span>
                    <div className="min-w-0 flex-1 text-[13px] leading-snug">
                        <p className="font-semibold">{titulo}</p>
                        <p className="mt-0.5 opacity-90">{texto}</p>
                    </div>
                </div>
                {botao && <div className="shrink-0 sm:self-center">{botao}</div>}
            </div>
        </div>
    );
}
